import React, { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react'
import { Modal, View, TextInput, ActionIcon, Text } from '@/components'
import { MentionSuggestionsList } from '@/components/chat'
import { PropsOf } from '@codeleap/types'
import { createStyles } from '@codeleap/styles'
import { Chat, textVariant } from '@/config'
import { ChatLocalMesage } from '@packages/chat'
import { onUpdate } from '@codeleap/hooks'
import { alert } from '@codeleap/modals'
import { Channel } from 'stream-chat'
import { MentionUser } from '@packages/chat'

export type EditMessageModalProps = PropsOf<typeof Modal> & {
  message?: ChatLocalMesage
  channel?: Channel
}

const EditMessageModalComponent = (props: EditMessageModalProps) => {
  const { message, channel, ...modalProps } = props
  const [textValue, setTextValue] = useState<string>('')
  const [cursorPosition, setCursorPosition] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [mentionedUsers, setMentionedUsers] = useState<MentionUser[]>([])
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLElement | null>(null)

  const {
    suggestions,
    isActive: showSuggestions,
    selectMention,
    extractMentionedUsers,
  } = Chat.Hooks.useMentionSuggestions({
    channel,
    text: textValue,
    cursorPosition,
  })

  const { maxMentionsReached } = Chat.Hooks.useMentionsLimit(mentionedUsers, textValue)

  const normalizeText = useCallback((text: string) => text.replace(/^[ \t\u200B]+|[ \t\u200B]+$/g, ''), [])

  const initialText = useMemo(() => message?.text || '', [message?.text])
  const isFormValid = !!normalizeText(textValue).trim() && normalizeText(textValue).trim() !== normalizeText(initialText).trim()

  const handleSelectMention = useCallback((user: MentionUser) => {
    const newText = selectMention(user)
    setTextValue(newText)
    const mentionIndex = newText.indexOf(` ${user.name} `)
    const newCursorPosition = mentionIndex !== -1 ? mentionIndex + user.name.length + 2 : newText.length
    setCursorPosition(newCursorPosition)
    setMentionedUsers(state => [...state, user])
    setTimeout(() => {
      if (inputRef.current) {
        let inputElement: HTMLInputElement | HTMLTextAreaElement | null = null

        if (inputRef.current instanceof HTMLInputElement || inputRef.current instanceof HTMLTextAreaElement) {
          inputElement = inputRef.current
        } else if (inputRef.current instanceof HTMLElement) {
          inputElement = inputRef.current.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null
        }

        if (inputElement) {
          inputElement.focus()
          if (typeof inputElement.setSelectionRange === 'function') {
            inputElement.setSelectionRange(newCursorPosition, newCursorPosition)
          }
        }
      }
    }, 0)
  }, [selectMention, setMentionedUsers])

  const handleSelectionChange = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement
    setCursorPosition(target.selectionEnd || 0)
  }, [])

  useEffect(() => {
    if (message?.text && props.visible) {
      setTextValue(message.text)
    }
  }, [props.visible, message?.text])

  const handleSave = async () => {
    if (!message || !normalizeText(textValue)) return

    const trimmedText = normalizeText(textValue)
    if (Chat.Services.blockIfExcluded(trimmedText)) return

    setLoading(true)

    try {
      const mentionedUsers = extractMentionedUsers(trimmedText)
      await Chat.Services.updateMessage(message, trimmedText, mentionedUsers)
      Chat.Store.setToastMessage('Message edited')
      props?.toggle?.()
    } catch (error) {
      alert.error({
        title: 'Error',
        body: 'Failed to edit message',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (normalizeText(textValue) !== normalizeText(initialText)) {
      alert.ask({
        title: 'Discard edit?',
        body: 'Are you sure you want to discard this edit?',
        options: [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Discard',
            style: 'destructive',
            onPress: () => {
              setTextValue(initialText)
              props?.toggle?.()
            },
          },
        ],
      })
    } else {
      props?.toggle?.()
    }
  }

  onUpdate(() => {
    if (!props.visible) {
      setTextValue('')
    }
  }, [props.visible])

  const chatUser = Chat.Store.useUser()
  const isMyMessage = message?.user?.id === chatUser?.id

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  return (
    <Modal
      title='Edit message'
      style={['center', 'box:noPadding', { titleWrapper: styles.titleWrapper, box: styles.box, closeButtonIcon: styles.closeButtonIcon }]}
      showClose
      scrollLock
      {...modalProps}
      toggle={handleClose}
      onClick={handleModalClick}
    >
      <View style={styles.wrapper}>
        <View style={styles.content}>
          <View style={styles.messagePreviewCard}>
            <View style={[
              styles.messageBubble,
              isMyMessage && styles.messageBubbleMyMessage,
            ]}>
              <Text
                text={initialText}
                style={[
                  styles.messageText,
                  isMyMessage && styles.messageTextMyMessage,
                ]}
              />
            </View>
          </View>

        </View>
      </View>

      <View style={styles.inputContainer}>
        <MentionSuggestionsList
          suggestions={suggestions}
          onSelect={handleSelectMention}
          visible={showSuggestions}
        />

        <TextInput
          value={textValue}
          onValueChange={setTextValue}
          placeholder='Type your message'
          multiline
          style={['fullWidth', 'noError', 'height:large', { input: styles.input, 'innerWrapper:multiline': styles['innerWrapper:multiline'], 'innerWrapper:hasMultipleLines': styles['innerWrapper:hasMultipleLines'], 'input:multiline': styles['input:multiline'] }]}
          debugName='EditMessage:Input'
          autoFocus
          ref={inputRef}
          onSelect={handleSelectionChange}
          onClick={handleSelectionChange}
          onKeyUp={handleSelectionChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              if (isFormValid && !loading) {
                handleSave()
              }
            }
          }}
        />
        <ActionIcon
          icon='arrow-up'
          onPress={handleSave}
          disabled={!isFormValid || loading || maxMentionsReached}
          style={[
            'iconSize:2',
            styles.sendButton,
            (!isFormValid || loading) && styles.sendButtonDisabled,
          ]}
          debugName='EditMessage:Send'
        />
      </View>
    </Modal>
  )
}

const areEqual = (prev: EditMessageModalProps, next: EditMessageModalProps) => {
  if (prev.visible !== next.visible) return false
  if (prev.message?.id !== next.message?.id) return false
  if (prev.message?.text !== next.message?.text) return false
  if (prev.channel?.id !== next.channel?.id) return false
  return true
}

export const EditMessageModal = memo(EditMessageModalComponent, areEqual)

const styles = createStyles(theme => ({
  titleWrapper: {
    ...theme.spacing.padding(2),
    ...theme.spacing.paddingTop(4),
  },
  box: {
    maxWidth: 480,
    width: '100%',
  },
  closeButtonIcon: {
    color: theme.baseColors.neutralSolid1000,
  },
  wrapper: {
    ...theme.presets.column,
    width: '100%',
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  content: {
    ...theme.presets.column,
    ...theme.spacing.gap(2),
    ...theme.spacing.paddingHorizontal(2),
  },
  messagePreviewCard: {
    ...theme.presets.center,
    backgroundColor: theme.baseColors.neutralBlackTransparent50,
    borderRadius: theme.radius.regular,
    ...theme.spacing.padding(3),
    minHeight: 138,
  },
  messageInnerContainer: {
    ...theme.presets.row,
    ...theme.presets.relative,
    maxWidth: '80%',
  },
  messageInnerContainerMyMessage: {
    flexDirection: 'row-reverse',
  },
  messageBubble: {
    backgroundColor: theme.baseColors.primarySolid50,
    ...theme.spacing.padding(1.25),
    borderRadius: theme.radius.regular,
    position: 'relative',
  },
  messageBubbleMyMessage: {
    backgroundColor: theme.baseColors.primarySolid500,
    borderBottomLeftRadius: theme.radius.regular,
  },
  messageText: {
    ...textVariant('c1'),
    color: theme.colors.textHeadingRegular,
    lineHeight: 1.6,
    overflowWrap: 'anywhere',
    wordBreak: 'keep-all',
  },
  messageTextMyMessage: {
    color: theme.baseColors.neutralSolid0,
  },
  tailStyle: {
    color: theme.baseColors.primarySolid50,
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  tailStyleMyMessage: {
    top: 'auto',
    left: 'auto',
    right: 1,
    rotate: '90deg',
    color: theme.baseColors.primarySolid500,
  },
  inputContainer: {
    ...theme.presets.row,
    ...theme.presets.center,
    position: 'relative',
    width: '100%',
    gap: theme.spacing.value(2),
    ...theme.spacing.padding(2),
  },
  inputWrapper: {
    flex: 1,
  },
  input: {
    minHeight: 40,
    maxHeight: 52,
    resize: 'none',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    color: theme.baseColors.neutralSolid800,
    padding: 0,
  },
  'innerWrapper:hasMultipleLines': {
    minHeight: theme.size['large'],
    ...theme.spacing.paddingVertical(1.5),
    ...theme.presets.alignEnd
  },
  'input:multiline': {
    minHeight: theme.size.medium,
    maxHeight: 3 * theme.size.large,
  },
  sendButton: {
    width: theme.size.largeish,
    height: theme.size.largeish,
    minWidth: theme.size.largeish,
    minHeight: theme.size.largeish,
    maxWidth: theme.size.largeish,
    maxHeight: theme.size.largeish,
    ...theme.spacing.padding(0),
  },
  sendButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
}))
